import type { InvoicingSettings } from '../hooks/useGetSettings';
import { resolveInvoiceMismatchFlagFamilyPolicy } from './invoiceFlagFamilyPolicy';
import { resolveUnmatchedLineItemsNexSubmitMode } from './unmatchedLineItemsNexSubmitMode';

export const VENDOR_FLAGGING_INVOICE_TYPES = [
    'received',
    'returned',
    'return_slip',
    'sublet',
    '*',
] as const;

export type VendorFlaggingInvoiceType = (typeof VENDOR_FLAGGING_INVOICE_TYPES)[number];

export const VENDOR_FLAGGING_INVOICE_TYPE_OPTIONS = [
    { value: '*', label: 'All types' },
    { value: 'received', label: 'Received' },
    { value: 'returned', label: 'Credited' },
    { value: 'return_slip', label: 'Return slip' },
    { value: 'sublet', label: 'Sublet' },
] as const;

export function vendorFlaggingInvoiceTypeLabel(invoiceType: string | undefined): string {
    return (
        VENDOR_FLAGGING_INVOICE_TYPE_OPTIONS.find((option) => option.value === invoiceType)?.label ??
        invoiceType ??
        'All types'
    );
}

export function vendorFlaggingRuleKey(supplierId: string, invoiceType: string): string {
    return `${supplierId}~${invoiceType}`;
}

export function vendorFlaggingRulePath(ruleKey: string): string {
    return `/settings/invoicing-flags/${encodeURIComponent(`flag-rule~${ruleKey}`)}`;
}

export function parseVendorFlaggingRuleRowId(
    rowId: string,
): { isNew: true } | { isNew: false; supplierId: string; invoiceType: string } | undefined {
    if (rowId === 'flag-rule~new' || rowId === 'flag-rule') {
        return { isNew: true };
    }
    if (!rowId.startsWith('flag-rule~')) return undefined;
    const rest = rowId.slice('flag-rule~'.length);
    const at = rest.lastIndexOf('~');
    if (at <= 0) return undefined;
    const supplierId = rest.slice(0, at);
    const invoiceType = rest.slice(at + 1);
    if (!supplierId || !invoiceType) return undefined;
    return { isNew: false, supplierId, invoiceType };
}

type VendorFlaggingRule = NonNullable<InvoicingSettings['vendorFlaggingRules']>[number];

export function matchVendorFlaggingRule(input: {
    rules?: Array<VendorFlaggingRule> | null;
    supplierId?: string | null;
    invoiceType?: string | null;
}): VendorFlaggingRule | null {
    const supplierId = input.supplierId?.trim() ?? '';
    if (!supplierId || !input.rules?.length) return null;
    const invoiceType = input.invoiceType ?? null;
    const forVendor = input.rules.filter((rule) => rule.supplierId === supplierId);
    if (invoiceType) {
        const exact = forVendor.find((rule) => rule.invoiceType === invoiceType);
        if (exact) return exact;
    }
    return forVendor.find((rule) => rule.invoiceType === '*') ?? null;
}

export function overlayInvoicingSettingsWithVendorRule(
    invoicingSettings: InvoicingSettings | null | undefined,
    supplierId?: string | null,
    invoiceType?: string | null,
): InvoicingSettings | undefined {
    if (!invoicingSettings) return undefined;
    const matchedRule = matchVendorFlaggingRule({
        rules: invoicingSettings.vendorFlaggingRules,
        supplierId,
        invoiceType,
    });
    if (!matchedRule) return invoicingSettings;

    const copy: InvoicingSettings = { ...invoicingSettings };
    copy.unmatchedLineItemsNexSubmitMode = matchedRule.unmatchedLineItemsNexSubmitMode;
    copy.quantityMismatchFlaggingEnabled = matchedRule.quantityMismatchFlaggingEnabled;
    copy.priceMismatchFlaggingEnabled = matchedRule.priceMismatchFlaggingEnabled;
    copy.amountMismatchFlaggingEnabled = matchedRule.amountMismatchFlaggingEnabled;
    if (typeof matchedRule.amountMismatchFlagBothDirections === 'boolean') {
        copy.amountMismatchFlagBothDirections = matchedRule.amountMismatchFlagBothDirections;
    }

    const percent = matchedRule.amountMismatchPercentThreshold;
    const baseDollar = matchedRule.amountMismatchBaseDollarThreshold;
    const dollar = matchedRule.amountMismatchDollarThreshold ?? baseDollar;
    if (percent != null || baseDollar != null || matchedRule.amountMismatchDollarThreshold != null) {
        const applyReceived = () => {
            if (percent != null) copy.receivedAmountMismatchPercentThreshold = percent;
            if (baseDollar != null) copy.receivedAmountMismatchBaseDollarThreshold = baseDollar;
            if (dollar != null) copy.receivedAmountMismatchDollarThreshold = dollar;
        };
        const applyReturned = () => {
            if (percent != null) copy.returnedAmountMismatchPercentThreshold = percent;
            if (baseDollar != null) copy.returnedAmountMismatchBaseDollarThreshold = baseDollar;
            if (dollar != null) copy.returnedAmountMismatchDollarThreshold = dollar;
        };
        if (matchedRule.invoiceType === '*' || invoiceType == null) {
            applyReceived();
            applyReturned();
        } else if (invoiceType === 'returned') {
            applyReturned();
        } else {
            applyReceived();
        }
    }

    return copy;
}

export function resolveEffectiveInvoiceFlaggingSettings(
    invoicingSettings: InvoicingSettings | null | undefined,
    supplierId?: string | null,
    invoiceType?: string | null,
) {
    const effective = overlayInvoicingSettingsWithVendorRule(
        invoicingSettings,
        supplierId,
        invoiceType,
    );
    return {
        invoicingSettings: effective,
        unmatchedLineItemsNexSubmitMode: resolveUnmatchedLineItemsNexSubmitMode(effective),
        mismatchFlagFamilyPolicy: resolveInvoiceMismatchFlagFamilyPolicy(effective),
    };
}

export function vendorFlaggingRuleSummary(rule: VendorFlaggingRule): string {
    const unmatched =
        rule.unmatchedLineItemsNexSubmitMode === 'allow_manual_and_automatic'
            ? 'Allow unmatched auto-submit'
            : rule.unmatchedLineItemsNexSubmitMode === 'allow_manual'
                ? 'Allow unmatched manual submit'
                : 'Block unmatched submit';
    const parts = [
        unmatched,
        rule.quantityMismatchFlaggingEnabled ? 'Qty on' : 'Qty off',
        rule.priceMismatchFlaggingEnabled ? 'Price on' : 'Price off',
        rule.amountMismatchFlaggingEnabled ? 'Total on' : 'Total off',
    ];
    if (rule.amountMismatchFlaggingEnabled && rule.amountMismatchFlagBothDirections === false) {
        parts.push('Invoice higher only');
    }
    if (rule.requireManualReview) parts.unshift('Always review');
    return parts.join(' · ');
}
